import React from 'react';
import { Stack, Text, Pivot, PivotItem, ITheme, Panel, DefaultEffects } from '@fluentui/react';
import AreaList from 'features/Areas/AreaList/AreaList';
import Members from 'features/Members/Members';
import CampusSettings from 'features/CampusSettings/CampusSettings';
import Billing from 'features/Billing/Billing';
import ChangeLog from 'features/ChangeLog/ChangeLog';
import SetupAssistant from 'features/CampusSettings/SetupAssistant/SetupAssistant';
import OnboardingVideoPopup from 'features/OnboardingVideoPopup/OnboardingVideoPopup';
import { SetterOrUpdater } from 'recoil';
import { CampusDto } from 'services/client-api-wrapper/campus_api/responses.dto';
import { OrgBrandingDto } from 'services/client-api-wrapper/identity_provider/responses.dto';
import { getTextColorForBackground, IvicosColors } from 'kits/colorKit';
import IvicosStrings from 'kits/language/stringKit';
import defaultLobbyBackgroundImage from 'media/img/default-lobby.jpg';
import AreasPanel from 'features/Areas/AreaPanel/AreaPanel';
import InvitationsManager from 'features/Header/children/InvitationsManager/InvitationsManager';
import NotificationsLayer from 'features/NotificationsLayer/NotificationsLayer';
import TrialPeriodAboutToEndModal from 'components/TrialPeriodAboutToEndModal/TrialPeriodAboutToEndModal';
import LobbyRedirectModal from './LobbyRedirectModal';
import LobbyInactivityWarningModal from './LobbyInactivityWarningModal';

export interface IDashboardViewProps {
    selectedKey: string;
    theme: ITheme;
    campus?: CampusDto;
    isOpen: boolean;
    openPanel: () => void;
    dismissPanel: () => void;
    tabIsVisible: {
        areas: boolean;
        members: boolean;
        settings: boolean;
        billing: boolean;
        log: boolean;
    };
    setTabIsVisible: React.Dispatch<
        React.SetStateAction<{
            areas: boolean;
            members: boolean;
            settings: boolean;
            billing: boolean;
            log: boolean;
        }>
    >;
    handleLinkClick: (item?: PivotItem | undefined) => void;
    isAdmin?: boolean;
    branding?: OrgBrandingDto;
    pivotDisplaySmallDevice: string;
    burgerMenu: string;
    campusNameSmallDevice: string;
    stackTitleSmallDevice: string;
    smallDevice: boolean;
    invitationsManagerModal: boolean;
    setInvitationsManagerModal: SetterOrUpdater<boolean>;
    trialExpiringWarningDisplayedToday: boolean;
    isOrgInTrial: string;
    identityClaims: any;
    hideDashboardTabs: boolean;
}

const DashboardDesktopView: React.FC<any> = (props) => {
    const {
        selectedKey,
        isOpen,
        dismissPanel,
        tabIsVisible,
        setTabIsVisible,
        handleLinkClick,
        isAdmin,
        branding,
        trialExpiringWarningDisplayedToday,
        isOrgInTrial,
        invitationsManagerModal,
        setInvitationsManagerModal,
        hideDashboardTabs,
        theme,
        isWarningVisible,
        countdownSeconds,
        onStayLoggedIn
    } = props;

    const color = (branding && branding.orgColors[0]) || IvicosColors.Mittelblau;
    const textColorFromBg = getTextColorForBackground(color);
    const bgImage = (() => {
        if (!branding) return defaultLobbyBackgroundImage;
        if (branding.lobby_background_image === 'none') return defaultLobbyBackgroundImage;
        if (branding.lobby_background_image === undefined) return defaultLobbyBackgroundImage;
        if (branding.lobby_background_image === null) return defaultLobbyBackgroundImage;
        if (branding.lobby_background_image === '') return defaultLobbyBackgroundImage;
        return branding.lobby_background_image;
    })();

    const pivotStyles = {
        root: {
            color: textColorFromBg // Change default text color
        },
        link: {
            color: textColorFromBg // Change inactive tab color
        },
        linkIsSelected: {
            color: textColorFromBg // Change selected tab color
        }
    };

    const bgStyle: React.CSSProperties = {
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
    };

    return (
        <Stack aria-label="dashboard-background-image" style={bgStyle}>
            <LobbyRedirectModal themeColor={theme?.palette?.themePrimary} />
            <LobbyInactivityWarningModal
                isOpen={isWarningVisible}
                countdownSeconds={countdownSeconds}
                onStay={onStayLoggedIn}
                themeColor={theme?.palette?.themePrimary}
            />
            <AreasPanel />
            <NotificationsLayer />
            {invitationsManagerModal && <InvitationsManager setInvitationsManagerModal={setInvitationsManagerModal} />}
            {isAdmin && !trialExpiringWarningDisplayedToday && isOrgInTrial && <TrialPeriodAboutToEndModal />}
            {!hideDashboardTabs && (
                <>
                    <Stack
                        horizontal
                        verticalAlign="center"
                        style={{
                            padding: '4px 15px 4px 15px',
                            backgroundColor: color,
                            marginBottom: '16px',
                            marginTop: '16px',
                            marginRight: 'auto',
                            marginLeft: '30px',
                            borderRadius: '4px',
                            boxShadow: DefaultEffects.elevation8
                        }}
                    >
                        <Pivot aria-label="dashboard-tabs" selectedKey={selectedKey} onLinkClick={handleLinkClick} styles={pivotStyles}>
                            <PivotItem headerText={IvicosStrings.availableAreasTitle} itemKey="areas" />
                            {isAdmin && <PivotItem headerText={IvicosStrings.manageAccessTitle} itemKey="members" />}
                            {isAdmin && <PivotItem headerText={IvicosStrings.campusSettingsTitle} itemKey="settings" />}
                            {isAdmin && <PivotItem headerText={IvicosStrings.billingTitle} itemKey="billing" />}
                            <PivotItem headerText={IvicosStrings.changeLogTitle} itemKey="changelog" />
                        </Pivot>
                    </Stack>

                    <SetupAssistant />
                    <OnboardingVideoPopup />
                </>
            )}

            {selectedKey === 'areas' && (
                <Stack aria-label="dashboard-area-list" grow style={{ marginTop: hideDashboardTabs ? '50px' : '0px' }}>
                    <AreaList />
                </Stack>
            )}
            {isAdmin && selectedKey === 'members' && <Members />}
            {isAdmin && selectedKey === 'settings' && <CampusSettings />}
            {isAdmin && selectedKey === 'billing' && <Billing />}
            {selectedKey === 'changelog' && <ChangeLog />}

            <Panel
                isOpen={isOpen}
                onDismiss={dismissPanel}
                // You MUST provide this prop! Otherwise screen readers will just say "button" with no label.
                closeButtonAriaLabel={IvicosStrings.closeActionTitle}
            >
                <img src={branding && branding.orgLogo} style={{ width: '50%' }} />

                <Stack style={{ marginTop: '15px' }}>
                    <Stack>
                        <Text
                            variant="xLarge"
                            onClick={() => {
                                dismissPanel();
                                setTabIsVisible({ areas: true, members: false, settings: false, billing: false, log: false });
                            }}
                            style={
                                tabIsVisible.areas
                                    ? {
                                          fontSize: '27px',
                                          color: IvicosColors.Mittelblau,
                                          marginBottom: '35px',
                                          fontWeight: 'bolder'
                                      }
                                    : { marginBottom: '30px' }
                            }
                        >
                            {IvicosStrings.availableAreasTitle}
                        </Text>
                    </Stack>
                    {isAdmin && (
                        <Text
                            variant="xLarge"
                            onClick={() => {
                                dismissPanel();
                                setTabIsVisible({ areas: false, members: true, settings: false, billing: false, log: false });
                            }}
                            style={
                                tabIsVisible.members
                                    ? {
                                          fontSize: '27px',
                                          color: IvicosColors.Mittelblau,
                                          marginBottom: '35px',
                                          fontWeight: 'bolder'
                                      }
                                    : { marginBottom: '30px' }
                            }
                        >
                            {IvicosStrings.manageAccessTitle}
                        </Text>
                    )}
                    {isAdmin && (
                        <Text
                            variant="xLarge"
                            onClick={() => {
                                dismissPanel();
                                setTabIsVisible({ areas: false, members: false, settings: true, billing: false, log: false });
                            }}
                            style={
                                tabIsVisible.settings
                                    ? {
                                          fontSize: '27px',
                                          color: IvicosColors.Mittelblau,
                                          marginBottom: '35px',
                                          fontWeight: 'bolder'
                                      }
                                    : { marginBottom: '30px' }
                            }
                        >
                            {IvicosStrings.campusSettingsTitle}
                        </Text>
                    )}
                    {isAdmin && (
                        <Text
                            variant="xLarge"
                            onClick={() => {
                                dismissPanel();
                                setTabIsVisible({ areas: false, members: false, settings: false, billing: true, log: false });
                            }}
                            style={
                                tabIsVisible.billing
                                    ? {
                                          fontSize: '27px',
                                          color: IvicosColors.Mittelblau,
                                          marginBottom: '35px',
                                          fontWeight: 'bolder'
                                      }
                                    : { marginBottom: '30px' }
                            }
                        >
                            {IvicosStrings.billingTitle}
                        </Text>
                    )}
                    <Text
                        variant="xLarge"
                        onClick={() => {
                            dismissPanel();
                            setTabIsVisible({ areas: false, members: false, settings: false, billing: false, log: true });
                        }}
                        style={
                            tabIsVisible.log
                                ? {
                                      fontSize: '27px',
                                      color: IvicosColors.Mittelblau,
                                      marginBottom: '35px',
                                      fontWeight: 'bolder'
                                  }
                                : { marginBottom: '30px' }
                        }
                    >
                        {IvicosStrings.changeLogTitle}
                    </Text>
                </Stack>
            </Panel>
        </Stack>
    );
};

export default DashboardDesktopView;
